import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Container, Row, Col, Label, Spinner, Badge } from "reactstrap";
import Select from "react-select";
import { useParams, Link } from "react-router-dom";
import { getAuthHeaders, getCurrentUser } from "../utils/auth";
import exportProyectoExcel from "../utils/exportProyectoExcel";
import { recomputeAsignacionUtilidades } from "../utils/asignacion-utilidad";


import {
  API_URL_proyectos,
  API_URL_instructores,
  API_URL_partidas,
  API_URL_partidas_curso,
  API_URL_partidas_curso_abierto,
  API_URL_partners,
  API_URL_comision_multi,
  API_URL_utilidades_partners,
  API_URL_utilidades_byproject,
  API_URL_abierto_miembros,
  API_URL_lideres_profit,
  API_URL_sedes,
  API_URL_proyecto_summary,
} from "../constants";

import ProyectoIngresoModal from "../components/ProyectoDetail/Instructor/ProyectoIngresoModal";
import ProyectoCostoModal from "../components/ProyectoDetail/Partidas/ProyectoCostoModal";
import ProyectoDetailInstructorList from "../components/ProyectoDetail/Instructor/ProyectoDetailInstructorList";
import ProyectoDetailPartidasList from "../components/ProyectoDetail/Partidas/ProyectoDetailPartidasList";
import ProyectoDetailCursoList from "../components/ProyectoDetail/CursoCerradoEstudiantes/ProyectoDetailCursoList";
import ProyectoCursoModal from "../components/ProyectoDetail/CursoCerradoEstudiantes/ProyectoCursoModal";
import HonorariosList from "../components/ProyectoDetail/Honorarios/HonorariosList";
import CursoAbiertoList from "../components/ProyectoDetail/CursoAbierto/CursoAbiertoList";
import CursoAbiertoModal from "../components/ProyectoDetail/CursoAbierto/CursoAbiertoModal";
import AdminCursoList from "../components/ProyectoDetail/Admin de Curso/AdminCursoList";
import AdminCursoModal from "../components/ProyectoDetail/Admin de Curso/AdminCursoModal";
import AdminCursoModalCerrado from "../components/ProyectoDetail/Admin de Curso Cerrado/AdminCursoModalCerrado";
import AdminCursoListCerrado from "../components/ProyectoDetail/Admin de Curso Cerrado/AdminCursoListCerrado";
import IngresoGeneralModal from "../components/ProyectoDetail/Ingreso General/IngresoGeneralModal";
import IngresoGeneralList from "../components/ProyectoDetail/Ingreso General/IngresoGeneralList";
import ProyectoConCausaList from "../components/ProyectoDetail/Proyecto con Causa/ProyectoConCausaList";
import ProyectoConCausaModal from "../components/ProyectoDetail/Proyecto con Causa/ProyectoConCausaModal";
import ComisionVentaModal from "../components/ProyectoDetail/Comision de Venta/ComisionVentaModal";
import ComisionVentaList from "../components/ProyectoDetail/Comision de Venta/ComisionVentaList";
import { formatNumber } from "../components/utils";
import CurrencyDisplay from "../components/CurrencyDisplay";
import SummaryCards from "../components/ProyectoDetail/SummaryCards";
import {
  getStagePercentages,
  getAssignmentPoolPercent,
  isExcludedFromAssignment
} from "../utils/asignacion-utilidad";
import "./styles.css";

// ---------- helpers ----------
const toSelectOptions = (arr) => arr.map((v) => ({ value: v, label: v }));

// Portal react-select menus to <body> so they escape the selector card's
// stacking context and are never covered by siblings below it (empty state,
// KPI cards, etc.).
const SELECT_MENU_PORTAL_PROPS = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};
const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

// Canonicalize country names so equivalent labels (e.g. "USA" / "United States")
// collapse to a single option in filters and dropdowns.
const COUNTRY_ALIASES = {
  usa: "United States",
  "u.s.a.": "United States",
  "u.s.": "United States",
  us: "United States",
  "united states of america": "United States",
};
const normalizeCountry = (v) => {
  const raw = (v ?? "").toString().trim();
  if (!raw) return raw;
  return COUNTRY_ALIASES[raw.toLowerCase()] || raw;
};

// ---------- Loading Skeleton Component ----------
const SectionSkeleton = () => (
  <div className="section-skeleton">
    <div className="skeleton-line" style={{ width: '60%', height: '30px' }}></div>
    <div className="skeleton-line" style={{ width: '40%', height: '20px', marginTop: '10px' }}></div>
    <div className="skeleton-line" style={{ width: '80%', height: '20px', marginTop: '10px' }}></div>
    <div className="skeleton-line" style={{ width: '50%', height: '20px', marginTop: '10px' }}></div>
  </div>
);

// ---------- Status Badge Component ----------
const StatusBadge = ({ count, type }) => {
  const colors = {
    instructores: '#28a745',
    partidas: '#ffc107',
    cursos: '#17a2b8',
    comisiones: '#6f42c1',
    empty: '#6c757d'
  };
  
  return (
    <span 
      className="badge status-badge" 
      style={{ 
        backgroundColor: count > 0 ? colors[type] : colors.empty,
      }}
    >
      {count}
    </span>
  );
};

// ---------- Section Card (replaces the old collapsible blue-gradient header)
// Flat white card with a small colored bar, icon, title, and count chip.
// Content is always visible — tabs handle the "show one thing at a time" role
// that the old collapsible headers used to.
const SectionCard = ({ title, icon, count, type, searchHighlight = false, children }) => {
  const accent =
    type === 'instructores' ? '#0369a1'
    : type === 'cursos' ? '#7c3aed'
    : type === 'partidas' ? '#0e7c66'
    : type === 'comisiones' ? '#d97706'
    : '#dc3545';
  return (
    <section
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.03)',
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid #e5e7eb',
          background: searchHighlight ? '#fffbeb' : '#fafafa',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 4,
            height: 22,
            background: accent,
            borderRadius: 2,
          }}
          aria-hidden="true"
        />
        {icon && (
          <span style={{ display: 'inline-flex', color: accent }} aria-hidden="true">{icon}</span>
        )}
        <h2
          style={{
            margin: 0,
            fontSize: '1.05rem',
            fontWeight: 600,
            color: '#111827',
            letterSpacing: '-0.005em',
            flex: 1,
          }}
        >
          {title}
        </h2>
        {count !== undefined && count !== null && (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: accent,
              background: `${accent}14`,
              padding: '3px 10px',
              borderRadius: 999,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {count}
          </span>
        )}
        {searchHighlight && (
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: '#a16207',
              background: '#fef3c7',
              padding: '3px 8px',
              borderRadius: 4,
            }}
          >
            Coincidencias
          </span>
        )}
      </header>
      <div style={{ padding: 18 }}>{children}</div>
    </section>
  );
};

// ---------- Icon set for section headers and tabs
const IconEquipo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconCurso = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const IconPartida = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);
const IconHonorarios = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const IconExport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// ---------- Small metadata chip (label + value) for the project header row
const MetaChip = ({ label, value }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      background: '#f4f4f5',
      border: '1px solid #e5e7eb',
      borderRadius: 999,
      fontSize: '0.8rem',
      color: '#374151',
      lineHeight: 1.4,
    }}
  >
    <span style={{ color: '#6b7280', fontWeight: 500 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 600 }}>{value}</span>
  </span>
);

// ---------- Single tab button in the top strip
const TabButton = ({ active, onClick, icon, label, count, highlight }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 16px',
      border: 'none',
      background: 'transparent',
      color: active ? '#a01e2b' : '#4b5563',
      fontWeight: active ? 600 : 500,
      fontSize: '0.9rem',
      cursor: 'pointer',
      borderBottom: `2px solid ${active ? '#dc3545' : 'transparent'}`,
      transition: 'color 0.15s ease, border-color 0.15s ease',
      position: 'relative',
    }}
  >
    {icon}
    {label}
    {count !== undefined && count !== null && (
      <span
        style={{
          fontSize: '0.7rem',
          fontWeight: 600,
          color: active ? '#a01e2b' : '#6b7280',
          background: active ? 'rgba(220,53,69,0.12)' : '#f3f4f6',
          padding: '1px 8px',
          borderRadius: 999,
          minWidth: 20,
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count}
      </span>
    )}
    {highlight && (
      <span
        aria-hidden="true"
        title="Coincidencias de búsqueda"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: '#f59e0b',
          marginLeft: 2,
        }}
      />
    )}
  </button>
);

// ---------- Sticky Summary Bar (Fixed positioning at top) ----------
const StickySummaryBar = ({ ingreso, costo, utilidad, margen, visible, proyectoName }) => {
  if (!visible) return null;
  
  const getMarginColor = () => {
    if (margen >= 80) return "#28a745";
    if (margen >= 50) return "#17a2b8";
    if (margen >= 20) return "#ffc107";
    return "#dc3545";
  };
  
  return (
    <div 
      className="sticky-summary-bar"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1050,
        backgroundColor: '#fff',
        borderBottom: '2px solid #dee2e6',
        padding: '10px 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '30px',
        flexWrap: 'wrap'
      }}
    >
      {proyectoName && (
        <div style={{ fontWeight: 600, color: '#333', marginRight: '20px' }}>
          📋 {proyectoName.length > 30 ? proyectoName.substring(0, 30) + '...' : proyectoName}
        </div>
      )}
      <div style={{ textAlign: 'center' }}>
        <small className="text-muted d-block" style={{ fontSize: '0.7em' }}>Ingreso</small>
        <strong className="text-success" style={{ fontSize: '0.95em' }}>${formatNumber(ingreso)}</strong>
      </div>
      <div style={{ textAlign: 'center' }}>
        <small className="text-muted d-block" style={{ fontSize: '0.7em' }}>Costo</small>
        <strong className="text-danger" style={{ fontSize: '0.95em' }}>${formatNumber(costo)}</strong>
      </div>
      <div style={{ textAlign: 'center' }}>
        <small className="text-muted d-block" style={{ fontSize: '0.7em' }}>Utilidad</small>
        <strong className="text-info" style={{ fontSize: '0.95em' }}>${formatNumber(utilidad)}</strong>
      </div>
      <div style={{ textAlign: 'center' }}>
        <small className="text-muted d-block" style={{ fontSize: '0.7em' }}>Margen</small>
        <strong style={{ color: getMarginColor(), fontSize: '0.95em' }}>{formatNumber(margen)}%</strong>
      </div>
    </div>
  );
};

// ---------- FiltersRow component ----------
function FiltersRow({
  modalidadOptions,
  liderOptions,
  statusOptions,
  cityOptions,
  countryOptions,
  sedes,
  selectedModalidad,
  setSelectedModalidad,
  selectedLider,
  setSelectedLider,
  selectedStatus,
  setSelectedStatus,
  selectedCity,
  setSelectedCity,
  selectedCountry,
  setSelectedCountry,
  selectedSede,
  setSelectedSede,
}) {
  const modalidadSelectOptions = useMemo(
    () => toSelectOptions(modalidadOptions),
    [modalidadOptions]
  );
  const liderSelectOptions = useMemo(
    () => toSelectOptions(liderOptions),
    [liderOptions]
  );
  const statusSelectOptions = useMemo(
    () => toSelectOptions(statusOptions),
    [statusOptions]
  );
  const citySelectOptions = useMemo(
    () => toSelectOptions(cityOptions),
    [cityOptions]
  );
  const countrySelectOptions = useMemo(
    () => toSelectOptions(countryOptions),
    [countryOptions]
  );

  const sedeSelectOptions = useMemo(
    () => sedes.map((s) => ({ value: s.sede, label: s.sede })),
    [sedes]
  );

  return (
    <>
      <Row>
        <Col>
          <Label>Selecciona Modalidad</Label>
          <Select
            options={modalidadSelectOptions}
            isClearable
            isSearchable
            placeholder="Todas"
            value={
              selectedModalidad
                ? { value: selectedModalidad, label: selectedModalidad }
                : null
            }
            onChange={(o) => setSelectedModalidad(o?.value || null)}
            {...SELECT_MENU_PORTAL_PROPS}
          />
        </Col>
        <Col>
          <Label>Selecciona Líder</Label>
          <Select
            options={liderSelectOptions}
            isClearable
            isSearchable
            placeholder="Todos"
            value={
              selectedLider ? { value: selectedLider, label: selectedLider } : null
            }
            onChange={(o) => setSelectedLider(o?.value || null)}
            {...SELECT_MENU_PORTAL_PROPS}
          />
        </Col>
        <Col>
          <Label>Selecciona Status</Label>
          <Select
            options={statusSelectOptions}
            isClearable
            isSearchable
            placeholder="Todos"
            value={
              selectedStatus
                ? { value: selectedStatus, label: selectedStatus }
                : null
            }
            onChange={(o) => setSelectedStatus(o?.value || null)}
            {...SELECT_MENU_PORTAL_PROPS}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 20 }}>
        <Col>
          <Label>Selecciona Ciudad</Label>
          <Select
            options={citySelectOptions}
            isClearable
            isSearchable
            placeholder="Todas"
            value={
              selectedCity ? { value: selectedCity, label: selectedCity } : null
            }
            onChange={(o) => setSelectedCity(o?.value || null)}
            {...SELECT_MENU_PORTAL_PROPS}
          />
        </Col>
        <Col>
          <Label>Selecciona País</Label>
          <Select
            options={countrySelectOptions}
            isClearable
            isSearchable
            placeholder="Todos"
            value={
              selectedCountry
                ? { value: selectedCountry, label: selectedCountry }
                : null
            }
            onChange={(o) => setSelectedCountry(o?.value || null)}
            {...SELECT_MENU_PORTAL_PROPS}
          />
        </Col>
        <Col>
          <Label>Selecciona Value Stream</Label>
          <Select
            options={sedeSelectOptions}
            isClearable
            isSearchable
            placeholder="Todas"
            value={
              selectedSede ? { value: selectedSede, label: selectedSede } : null
            }
            onChange={(o) => setSelectedSede(o?.value || null)}
            {...SELECT_MENU_PORTAL_PROPS}
          />
        </Col>
      </Row>
    </>
  );
}

// ---------- main component ----------
function DetalleProyectos() {
  const { proyectoCode } = useParams();
  const currentUser = getCurrentUser();
  const isAdmin = [
    "Salvador", "Bernardo", "Luis",
    "salvador@lidigo.io",
  ].includes(currentUser);
  const headers = useMemo(() => getAuthHeaders(), []);

  // Core state
  const [proyectos, setProyectos] = useState([]);
  const [selectedProyecto, setSelectedProyecto] = useState(null);

  // Lists / tables
  const [instructores, setInstructores] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [partidasCurso, setPartidasCurso] = useState([]);
  const [estudiantesAbierto, setEstudiantesAbierto] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [adminsCerrado, setAdminsCerrado] = useState([]);
  const [ingresoGeneral, setIngresoGeneral] = useState([]);
  const [proyectoConCausa, setProyectoConCausa] = useState([]);
  const [comisionVenta, setComisionVenta] = useState([]);
  const [partners, setPartners] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [utilidadesByProject, setUtilidadesByProject] = useState([]);
  const [abiertoMiembros, setAbiertoMiembros] = useState([]);
  const [filteredLider, setFilteredLider] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [loadedLeader, setLoadedLeader] = useState(null); // Leader from Coaching/Líder in utilities

  // Ref for HonorariosList to access calculateCompleteDistribution
  const honorariosListRef = useRef(null);

  // Backend summary
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Filters
  const [selectedModalidad, setSelectedModalidad] = useState(null);
  const [selectedLider, setSelectedLider] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedSede, setSelectedSede] = useState(null);

  // UI State
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Tabs replace the old "one long scroll of collapsible sections" layout.
  // Default lands on the first tab; changing project or clearing selection
  // resets nothing — the user stays where they were within the same page.
  const [activeTab, setActiveTab] = useState('instructores');
  // Selector (filters + project dropdown) is expanded when nothing is loaded
  // and auto-collapses once a project is picked; a "Cambiar proyecto" toggle
  // reopens it.
  const [selectorOpen, setSelectorOpen] = useState(true);

  // Scroll handlers
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 300);
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============================================
  // SEARCH FUNCTIONALITY - Filter data by search term
  // ============================================
  const searchFilter = useCallback((item, fields) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return fields.some(field => {
      const value = item[field];
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(term);
    });
  }, [searchTerm]);

  // Filtered instructores
  const filteredInstructores = useMemo(() => {
    return instructores.filter(item => 
      searchFilter(item, ['instructor_name', 'instructor', 'email', 'modalidad'])
    );
  }, [instructores, searchFilter]);

  // Filtered partidas
  const filteredPartidas = useMemo(() => {
    return partidas.filter(item => 
      searchFilter(item, ['concepto', 'descripcion', 'partner', 'categoria'])
    );
  }, [partidas, searchFilter]);

  // Filtered estudiantes abierto
  const filteredEstudiantesAbierto = useMemo(() => {
    return estudiantesAbierto.filter(item => 
      searchFilter(item, ['nombre', 'email', 'empresa', 'vendedor'])
    );
  }, [estudiantesAbierto, searchFilter]);

  // Filtered partidas curso (cerrado)
  const filteredPartidasCurso = useMemo(() => {
    return partidasCurso.filter(item => 
      searchFilter(item, ['concepto', 'descripcion', 'cliente'])
    );
  }, [partidasCurso, searchFilter]);

  // Filtered ingreso general
  const filteredIngresoGeneral = useMemo(() => {
    return ingresoGeneral.filter(item => 
      searchFilter(item, ['descripcion', 'partner', 'concepto'])
    );
  }, [ingresoGeneral, searchFilter]);

  // Filtered proyecto con causa
  const filteredProyectoConCausa = useMemo(() => {
    return proyectoConCausa.filter(item => 
      searchFilter(item, ['descripcion', 'partner', 'causa'])
    );
  }, [proyectoConCausa, searchFilter]);

  // Filtered comision venta
  const filteredComisionVenta = useMemo(() => {
    return comisionVenta.filter(item => 
      searchFilter(item, ['descripcion', 'partner', 'vendedor'])
    );
  }, [comisionVenta, searchFilter]);

  // Check which sections have search results
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return {};
    return {
      instructores: filteredInstructores.length > 0 && filteredInstructores.length !== instructores.length,
      partidas: filteredPartidas.length > 0 && filteredPartidas.length !== partidas.length,
      cursoAbierto: filteredEstudiantesAbierto.length > 0 && filteredEstudiantesAbierto.length !== estudiantesAbierto.length,
      cursoCerrado: filteredPartidasCurso.length > 0 && filteredPartidasCurso.length !== partidasCurso.length,
      ingresoGeneral: filteredIngresoGeneral.length > 0 && filteredIngresoGeneral.length !== ingresoGeneral.length,
      proyectoConCausa: filteredProyectoConCausa.length > 0 && filteredProyectoConCausa.length !== proyectoConCausa.length,
      comisionVenta: filteredComisionVenta.length > 0 && filteredComisionVenta.length !== comisionVenta.length,
    };
  }, [searchTerm, filteredInstructores, filteredPartidas, filteredEstudiantesAbierto, filteredPartidasCurso, filteredIngresoGeneral, filteredProyectoConCausa, filteredComisionVenta, instructores, partidas, estudiantesAbierto, partidasCurso, ingresoGeneral, proyectoConCausa, comisionVenta]);

  // Total search results count - count matching items across all searchable sections
  const totalSearchResults = useMemo(() => {
    if (!searchTerm.trim()) return 0;
    // When a search term is active, filtered arrays only contain matching items
    // Count all items that survived the filter (these are the matches)
    const allOriginal = instructores.length + partidas.length + estudiantesAbierto.length +
      partidasCurso.length + ingresoGeneral.length + proyectoConCausa.length + comisionVenta.length;
    const allFiltered = filteredInstructores.length + filteredPartidas.length + filteredEstudiantesAbierto.length +
      filteredPartidasCurso.length + filteredIngresoGeneral.length + filteredProyectoConCausa.length + filteredComisionVenta.length;
    // Only show count if filtering actually removed some items
    return allFiltered < allOriginal ? allFiltered : 0;
  }, [searchTerm, filteredInstructores, filteredPartidas, filteredEstudiantesAbierto, filteredPartidasCurso, filteredIngresoGeneral, filteredProyectoConCausa, filteredComisionVenta, instructores, partidas, estudiantesAbierto, partidasCurso, ingresoGeneral, proyectoConCausa, comisionVenta]);

  // Helper to calculate profit distribution without component ref
  const calculateDistributionForExport = () => {
    const utilizadNum = Number(utilidad) || 0;
    const STAGE_DISPLAY_NAMES = {
      contacto: "Contacto",
      cita: "Cita",
      diagnostico: "Diagnóstico",
      propuesta: "Propuesta",
      cierre: "Cierre",
      kickoff: "Kickoff",
      seguimiento: "Seguimiento",
      reunioncierre: "Reunión de Cierre",
      coaching: "Coaching/Líder",
    };

    const getStageCategory = (key) => {
      if (['contacto', 'cita'].includes(key)) return 'Preventa';
      if (['diagnostico', 'propuesta', 'cierre'].includes(key)) return 'Comercial';
      if (key === 'coaching') return 'Coaching';
      return 'Post Venta';
    };

    // 1. Build stage breakdown from utilities data
    const stageBreakdown = [];
    const stagePercentages = getStagePercentages();
    let totalStages = 0;

    (utilities || []).forEach((util) => {
      const stage = util.tipo_utilidad;
      const partner = util.partner;
      const amount = Number(util.total_cantidad) || 0;
      const percent = stagePercentages[stage] || 0;

      if (partner && amount > 0 && stagePercentages[stage]) {
        stageBreakdown.push({
          stage: STAGE_DISPLAY_NAMES[stage] || stage,
          partner,
          percent,
          amount,
          category: getStageCategory(stage),
        });
        totalStages += amount;
      }
    });

    // 2. Calculate instructor assignments
    const assignments = [];
    const assignmentPoolPercent = getAssignmentPoolPercent();
    const assignmentPool = utilizadNum * (assignmentPoolPercent / 100);

    // Calculate total eligible income
    let totalEligibleIncome = 0;
    const incomeByPartner = {};

    (instructores || []).forEach((inst) => {
      const partner = (inst.miembro_que_asigna || "").trim();
      if (!partner || isExcludedFromAssignment(partner)) return;

      const horas = Number(inst.horas || 0);
      const semanas = Number(inst.semanas || 0);
      const tarifa = Number(inst.tarifa_hora || 0);
      const income = horas * semanas * tarifa;

      incomeByPartner[partner] = (incomeByPartner[partner] || 0) + income;
      totalEligibleIncome += income;
    });

    // Build assignments array
    if (totalEligibleIncome > 0 && assignmentPool > 0) {
      Object.entries(incomeByPartner).forEach(([partner, partnerIncome]) => {
        const proportion = partnerIncome / totalEligibleIncome;
        const commission = proportion * assignmentPool;
        const percentOfUtilidad = (commission / (utilizadNum || 1)) * 100;

        assignments.push({
          partner,
          income: partnerIncome,
          proportion: proportion * 100,
          commission,
          percentOfUtilidad,
        });
      });
    }

    // 3. Build partner summary
    const partnerSummary = {};

    // Add stages to partner summary
    stageBreakdown.forEach(({ partner, amount: amt, percent: pct }) => {
      if (!partnerSummary[partner]) {
        partnerSummary[partner] = {
          stageTotal: 0,
          stagePercent: 0,
          assignmentTotal: 0,
          assignmentPercent: 0,
          grandTotal: 0,
          grandPercent: 0,
        };
      }
      partnerSummary[partner].stageTotal += amt;
      partnerSummary[partner].stagePercent += pct;
    });

    // Add assignments to partner summary
    assignments.forEach(({ partner, commission, percentOfUtilidad }) => {
      if (!partnerSummary[partner]) {
        partnerSummary[partner] = {
          stageTotal: 0,
          stagePercent: 0,
          assignmentTotal: 0,
          assignmentPercent: 0,
          grandTotal: 0,
          grandPercent: 0,
        };
      }
      partnerSummary[partner].assignmentTotal += commission;
      partnerSummary[partner].assignmentPercent += percentOfUtilidad;
    });

    // Calculate grand totals per partner
    Object.keys(partnerSummary).forEach((partner) => {
      const p = partnerSummary[partner];
      p.grandTotal = p.stageTotal + p.assignmentTotal;
      p.grandPercent = utilizadNum > 0 ? (p.grandTotal / utilizadNum) * 100 : 0;
    });

    // Calculate totals
    const totalPartnersAmount = Object.values(partnerSummary).reduce(
      (sum, p) => sum + p.grandTotal, 0
    );
    const totalPartnersPercent = utilizadNum > 0 ? (totalPartnersAmount / utilizadNum) * 100 : 0;

    // LSSI remaining
    const lssiRemaining = utilizadNum - totalPartnersAmount;
    const lssiRemainingPercent = utilizadNum > 0 ? (lssiRemaining / utilizadNum) * 100 : 0;

    return {
      utilidad: utilizadNum,
      stageBreakdown,
      totalStages,
      totalStagesPercent: utilizadNum > 0 ? (totalStages / utilizadNum) * 100 : 0,
      assignments,
      totalAssignments: assignmentPool,
      totalAssignmentsPercent: assignmentPoolPercent,
      partnerSummary,
      totalPartnersAmount,
      totalPartnersPercent,
      lssiRemaining,
      lssiRemainingPercent,
    };
  };

    const handleExportExcel = () => {
  // DEBUG - remove after confirming
  console.log("=== EXPORT DEBUG ===");
  console.log("utilities length:", utilities?.length);
  console.log("utilities sample:", JSON.stringify(utilities?.slice(0, 2), null, 2));
  console.log("utilities fields:", utilities?.[0] ? Object.keys(utilities[0]) : "EMPTY");
  console.log("filteredLider:", JSON.stringify(filteredLider?.slice(0, 1), null, 2));
  console.log("loadedLeader:", loadedLeader);
  console.log("selectedProyecto.lider:", selectedProyecto?.lider);
  console.log("utilidad:", utilidad);
  console.log("DEBUG partidas sample:", JSON.stringify(partidas?.slice(0, 1), null, 2));
  console.log("DEBUG partidas fields:", partidas?.[0] ? Object.keys(partidas[0]) : "EMPTY");
  console.log("=== END DEBUG ===");

  exportProyectoExcel({
    selectedProyecto,
    summary,
    instructores,
    partidas,
    partidasCurso,
    estudiantesAbierto,
    ingresoGeneral,
    comisionVenta,
    proyectoConCausa,
    utilities,
    utilidadesByProject,
    abiertoMiembros,
    filteredLider,
    loadedLeader,
    primaryCurrency,
    profitByCurrency,
    profitInOriginalCurrency,
    revenueInOriginalCurrency,
    costInOriginalCurrency,
  });
};

  

  // Callback to receive leader from HonorariosList (Coaching/Líder)
  const handleLeaderLoaded = useCallback((leaderName) => {
    setLoadedLeader(leaderName);
  }, []);

  // Per-proyecto request helper
  const q = (proy) => ({
    params: proy?.proyecto_code ? { proyecto_code: proy.proyecto_code } : {},
    headers,
  });

  // API helpers (per proyecto)
  const fetchInstructoresForProyecto = (proy) =>
    proy ? axios.get(API_URL_instructores, q(proy)).then((r) => r.data) : Promise.resolve([]);

  const fetchPartidasForProyecto = (proy) =>
    proy ? axios.get(API_URL_partidas, q(proy)).then((r) => r.data) : Promise.resolve([]);

  const fetchPartidasCursoForProyecto = (proy) =>
    proy ? axios.get(API_URL_partidas_curso, q(proy)).then((r) => r.data) : Promise.resolve([]);

  const fetchEstudiantesAbiertoForProyecto = (proy) =>
    proy ? axios.get(API_URL_partidas_curso_abierto, q(proy)).then((r) => r.data) : Promise.resolve([]);

  const fetchComisionDataForProyecto = async (proy) => {
    if (!proy)
      return {
        admins: [],
        adminsCerrado: [],
        ingresoGeneral: [],
        proyectoConCausa: [],
        comisionVenta: [],
      };
    const { data } = await axios.get(API_URL_comision_multi, q(proy));
    const list = data || [];
    return {
      admins: list.filter((c) => c.pago_por === "Admin de Curso"),
      adminsCerrado: list.filter((c) => c.pago_por === "Admin de Curso Cerrado"),
      ingresoGeneral: list.filter((c) => c.pago_por === "Ingreso en General"),
      proyectoConCausa: list.filter((c) => c.pago_por === "Proyecto con Causa"),
      comisionVenta: list.filter((c) => c.pago_por === "Comision de Venta"),
    };
  };

  const fetchUtilitiesForProyecto = async (proy) => {
    if (!proy) return { utilities: [] };
    const { data } = await axios.get(API_URL_utilidades_partners, { headers });
    const filtered = (data || []).filter((u) => u.proyecto_code === proy.proyecto_code);
    return { utilities: filtered };
  };

  const fetchUtilidadesByProject = (proy) =>
    proy ? axios.get(API_URL_utilidades_byproject, q(proy)).then((r) => r.data) : Promise.resolve([]);

  const fetchAbiertoMiembros = (proy) =>
    proy ? axios.get(API_URL_abierto_miembros, q(proy)).then((r) => r.data) : Promise.resolve([]);

  const fetchFilteredLider = async (proy) => {
    if (!proy) return [];
    const { data } = await axios.get(API_URL_lideres_profit, { headers });
    const target = normalize(proy.lider);
    return (data || []).filter((ld) => normalize(ld.lider) === target);
  };

  const fetchSummary = (proy) => {
    if (!proy?.proyecto_code) return Promise.resolve(null);
    return axios
      .get(API_URL_proyecto_summary, { params: { proyecto_code: proy.proyecto_code }, headers })
      .then((r) => r.data);
  };

  // fetch all per-proyecto data with retry
  const fetchDataForProyecto = useCallback(
    async (proy, isRetry = false) => {
      if (!proy) return;
      setLoading(true);
      setErr(null);
      try {
        const [
          summaryData,
          instructoresData,
          partidasData,
          partidasCursoData,
          estudiantesAbiertoData,
          comisiones,
          { utilities: utilList },
          utilByProject,
          abiertoMiembrosData,
          filteredLiderData,
        ] = await Promise.all([
          fetchSummary(proy),
          fetchInstructoresForProyecto(proy),
          fetchPartidasForProyecto(proy),
          fetchPartidasCursoForProyecto(proy),
          fetchEstudiantesAbiertoForProyecto(proy),
          fetchComisionDataForProyecto(proy),
          fetchUtilitiesForProyecto(proy),
          fetchUtilidadesByProject(proy),
          fetchAbiertoMiembros(proy),
          fetchFilteredLider(proy),
        ]);

        setSummary(summaryData || null);
        setInstructores(instructoresData || []);
        setPartidas(partidasData || []);
        setPartidasCurso(partidasCursoData || []);
        setEstudiantesAbierto(estudiantesAbiertoData || []);
        setAdmins(comisiones.admins || []);
        setAdminsCerrado(comisiones.adminsCerrado || []);
        setIngresoGeneral(comisiones.ingresoGeneral || []);
        setProyectoConCausa(comisiones.proyectoConCausa || []);
        setComisionVenta(comisiones.comisionVenta || []);
        setUtilities(utilList || []);
        setUtilidadesByProject(utilByProject || []);
        setAbiertoMiembros(abiertoMiembrosData || []);
        setFilteredLider(filteredLiderData || []);

        // Auto-recalculate utility assignments in background, then refresh utilities
        if (proy?.proyecto_code && summaryData?.utilidad > 0) {
          const hasInstructors = Array.isArray(instructoresData) && instructoresData.length > 0;
          recomputeAsignacionUtilidades(proy.proyecto_code, headers, { recalculateStages: true, recalculateAssignments: true, hasInstructors })
            .then((result) => {
              if (result.saved > 0) {
                // Refresh utilities with updated amounts
                fetchUtilitiesForProyecto(proy).then(({ utilities: refreshed }) => {
                  setUtilities(refreshed || []);
                });
              }
            })
            .catch((err) => console.error("Auto-recalculate failed:", err));
        }

        // Reset retry count on success
        if (isRetry) setRetryCount(0);
      } catch (e) {
        console.error("Error in fetchDataForProyecto:", e);
        
        // More specific error messages
        let errorMsg = "No se pudo cargar el proyecto.";
        if (e.response?.status === 404) {
          errorMsg = "Proyecto no encontrado.";
        } else if (e.response?.status === 403) {
          errorMsg = "No tienes permisos para ver este proyecto.";
        } else if (e.response?.status === 500) {
          errorMsg = "Error del servidor. Por favor contacta al administrador.";
        } else if (e.code === 'ECONNABORTED') {
          errorMsg = "La solicitud tardó demasiado. Intenta de nuevo.";
        } else if (!navigator.onLine) {
          errorMsg = "Sin conexión a internet. Verifica tu conexión.";
        }
        
        setErr(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [headers]
  );

  // initial fetch of proyectos, partners, sedes
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [proyRes, partnersRes, sedesRes] = await Promise.all([
          axios.get(API_URL_proyectos, { headers }),
          axios.get(API_URL_partners, { headers }),
          axios.get(API_URL_sedes, { headers }),
        ]);

        if (!alive) return;

        const proys = proyRes.data || [];
        setProyectos(proys);

        setPartners(
          (partnersRes.data || [])
            .slice()
            .sort((a, b) => a.partner.localeCompare(b.partner))
        );

        setSedes(sedesRes.data || []);

        if (proyectoCode) {
          const found =
            proys.find((p) => String(p.proyecto_code) === String(proyectoCode)) ||
            null;
          if (found) setSelectedProyecto(found);
        }
      } catch (e) {
        console.error("Initial fetch error:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [headers, proyectoCode]);

  // keep selectedProyecto in sync with route changes
  useEffect(() => {
    if (!proyectoCode || !proyectos.length) return;
    const found =
      proyectos.find((p) => String(p.proyecto_code) === String(proyectoCode)) ||
      null;
    if (
      found &&
      (!selectedProyecto ||
        found.proyecto_code !== selectedProyecto.proyecto_code)
    ) {
      setSelectedProyecto(found);
    }
  }, [proyectoCode, proyectos, selectedProyecto]);

  // Reset loaded leader when project changes
  useEffect(() => {
    setLoadedLeader(null);
  }, [selectedProyecto?.proyecto_code]);

  // load per-proyecto data when selectedProyecto changes
  useEffect(() => {
    if (selectedProyecto) fetchDataForProyecto(selectedProyecto);
  }, [selectedProyecto, fetchDataForProyecto]);

  const resetState = useCallback(() => {
    if (selectedProyecto) fetchDataForProyecto(selectedProyecto);
  }, [selectedProyecto, fetchDataForProyecto]);

  // ----------- FILTER logic over proyectos -----------
  const filteredProyectos = useMemo(() => {
    return (proyectos || []).filter((p) => {
      if (selectedModalidad && normalize(p.modalidad) !== normalize(selectedModalidad))
        return false;
      if (selectedLider && normalize(p.lider) !== normalize(selectedLider))
        return false;
      if (selectedStatus && normalize(p.status) !== normalize(selectedStatus))
        return false;
      if (selectedCity && normalize(p.ciudad) !== normalize(selectedCity))
        return false;
      if (
        selectedCountry &&
        normalize(normalizeCountry(p.pais)) !== normalize(normalizeCountry(selectedCountry))
      )
        return false;
      // p.sede is the numeric id; the dropdown works with names (sede_name).
      if (selectedSede && normalize(p.sede_name ?? p.sede) !== normalize(selectedSede))
        return false;
      return true;
    });
  }, [
    proyectos,
    selectedModalidad,
    selectedLider,
    selectedStatus,
    selectedCity,
    selectedCountry,
    selectedSede,
  ]);

  const proyectoOptions = useMemo(
    () =>
      filteredProyectos
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((proy) => ({ label: proy.name, value: proy })),
    [filteredProyectos]
  );

  // Reparto de utilidades
  const instructorsWithProportions = useMemo(() => {
    if (!instructores?.length) return [];
    const anyTarifaHora = instructores.some(
      (inst) => (inst.tarifa_hora || 0) > 0
    );

    const withProducts = instructores.map((inst) => {
      const rate =
        anyTarifaHora && (inst.tarifa_hora || 0) > 0
          ? inst.tarifa_hora
          : inst.costo_hora;
      const product =
        (inst.semanas || 0) * (inst.horas || 0) * (rate || 0);
      return { ...inst, product };
    });

    const totalProduct = withProducts.reduce(
      (sum, i) => sum + i.product,
      0
    );

    return withProducts.map((inst) => ({
      ...inst,
      proportion: totalProduct ? inst.product / totalProduct : 0,
    }));
  }, [instructores]);

  const instructorsWithTotalIncome = useMemo(() => {
    const util = summary?.utilidad || 0;
    return instructorsWithProportions.map((inst) =>
      inst.miembro_que_asigna !== "LSSI Europa"
        ? { ...inst, totalIncome: util * inst.proportion * 0.125 }
        : { ...inst, totalIncome: 0 }
    );
  }, [instructorsWithProportions, summary]);

  const handleProyectoSelect = (opt) => {
    setSelectedProyecto(opt ? opt.value : null);
    // Collapse the picker once a project is chosen so the detail can breathe;
    // clearing the selection re-opens it so the user isn't stuck.
    setSelectorOpen(!opt);
  };

  // Summary values
  const detalle = summary?.detalle || {};
  const totalIngreso = summary?.ingreso || 0;
  const totalCost = summary?.costo || 0;
  const utilidad = summary?.utilidad || 0;
  const margen = summary?.margen || 0;
  const currencyBreakdown = summary?.currency_breakdown || {};

  // New accurate profit by currency (from backend)
  const profitByCurrency = summary?.profit_by_currency || {};
  const primaryCurrency = summary?.primary_currency || selectedProyecto?.moneda || 'USD';

  // BUG-3: prefer primary_currency_totals — it converts the accurate USD
  // totals back to the project's primary currency using the monthly rate,
  // so a project mixing USD income and MXN cost no longer shows a bogus
  // negative MXN utilidad. Fall back to the per-currency bucket only when
  // the backend hasn't computed the primary totals (older payload).
  const primaryTotals = summary?.primary_currency_totals || null;
  const profitInOriginalCurrency = primaryTotals?.profit ?? profitByCurrency[primaryCurrency]?.profit ?? null;
  const revenueInOriginalCurrency = primaryTotals?.revenue ?? profitByCurrency[primaryCurrency]?.revenue ?? null;
  const costInOriginalCurrency = primaryTotals?.cost ?? profitByCurrency[primaryCurrency]?.cost ?? null;

  // Box Score Utilidad breakdown — reflect what's ACTUALLY been assigned to
  // partners (per the Utilidades table), not the theoretical 50/50 design.
  // Salvador reported that showing 50/50 in the Box Score while Honorarios
  // showed the real 39.30/60.70 for Caslon Etapa 3 was misleading — the two
  // views need to agree.
  //
  // Source of truth: the Utilidades table (auto-recomputed on load via
  // recomputeAsignacionUtilidades). Sum every row where partner ≠ "LSSI"; the
  // rest is LSSI. If no assignments exist yet (e.g. brand-new project with
  // zero utility), fall back to the design 50/50 so a fresh project still
  // reads as balanced instead of "100% LSSI".
  const assignedToPartners = (utilities || []).reduce((sum, u) => {
    const partner = (u.partner || '').trim();
    if (!partner || partner === 'LSSI') return sum;
    return sum + (Number(u.total_cantidad) || 0);
  }, 0);
  // Only trust the assigned totals when a STAGE utilidad has a partner on it.
  // The 12.5% instructor-assignment pool alone (auto-computed on save) can
  // produce a misleading tiny partner share when the 37.5% stage pool hasn't
  // been distributed yet — e.g. Solave Etapa 2 showed Partners 7% / LSSI 93%
  // with a single assignment record and zero stage rows. Falling back to the
  // 50/50 design in that case reads as "not yet assigned", not "LSSI-heavy".
  const STAGE_TYPES = new Set([
    'contacto', 'cita', 'diagnostico', 'propuesta', 'cierre',
    'kickoff', 'seguimiento', 'reunioncierre', 'coaching',
  ]);
  const hasStagePartnerAssignments = (utilities || []).some((u) => {
    const partner = (u.partner || '').trim();
    const tipo = (u.tipo_utilidad || '').trim().toLowerCase();
    return partner && partner !== 'LSSI'
      && Number(u.total_cantidad) > 0
      && STAGE_TYPES.has(tipo);
  });
  const utilidadPartners = hasStagePartnerAssignments ? assignedToPartners : utilidad * 0.5;
  const utilidadLSSI = utilidad - utilidadPartners;

  // ---- Revenue drill-down: per-source detail rows for the Box Score
  // Ingreso Total card. Each key matches a source in SummaryCards'
  // revenueBreakdown; SummaryCards renders these under the parent line
  // when the card is expanded. Amounts are shown in each row's native
  // currency (with the code) because the client can't reliably convert
  // to USD without the backend's exchange snapshot — the parent totals
  // above still come from the USD-converted backend summary. --------
  const money = (n) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n) || 0);
  const revenueDetails = {
    instructores: (instructores || []).map((i) => {
      const hrs = Number(i.horas) || 0;
      const rate = Number(i.tarifa_hora) || 0;
      const income = hrs * rate;
      const moneda = i.moneda || 'USD';
      return {
        label: i.instructor || '(sin nombre)',
        sub: `${money(hrs)} h × ${moneda} ${money(rate)}`,
        value: income,
        moneda,
      };
    }).filter((r) => r.value > 0),

    cursoCerrado: (partidasCurso || []).map((c) => {
      const students = Number(c.cantidad) || 0;
      const price = Number(c.costo) || 0;
      const income = students * price;
      const moneda = c.moneda || 'USD';
      return {
        label: c.nivel || c.partida_code || 'Grupo',
        sub: `${money(students)} estudiantes × ${moneda} ${money(price)}`,
        value: income,
        moneda,
      };
    }).filter((r) => r.value > 0),

    partidas: (partidas || []).map((p) => {
      const qty = Number(p.cantidad ?? 1) || 1;
      const price = Number(p.precio) || 0;
      const income = qty * price;
      const moneda = p.moneda || 'USD';
      return {
        label: p.partida || 'Partida',
        sub: qty !== 1 ? `${money(qty)} × ${moneda} ${money(price)}` : `${moneda} ${money(price)}`,
        value: income,
        moneda,
      };
    }).filter((r) => r.value > 0),

    cursoAbierto: (() => {
      // Group open-course students by name (the course they enrolled in).
      const buckets = new Map();
      (estudiantesAbierto || []).forEach((s) => {
        const key = s.nombre || 'Curso abierto';
        const moneda = s.moneda || 'USD';
        const price = Number(s.precio) || 0;
        const b = buckets.get(key) || { label: key, count: 0, income: 0, moneda };
        b.count += 1;
        b.income += price;
        buckets.set(key, b);
      });
      return Array.from(buckets.values())
        .filter((b) => b.income > 0)
        .map((b) => ({
          label: b.label,
          sub: `${b.count} estudiante${b.count === 1 ? '' : 's'}`,
          value: b.income,
          moneda: b.moneda,
        }));
    })(),

    ingresoGeneral: (ingresoGeneral || []).map((g) => {
      const amt = Number(g.monto ?? g.cantidad ?? g.ingreso) || 0;
      const moneda = g.moneda || 'USD';
      return {
        label: g.concepto || g.nombre || g.descripcion || 'Ingreso',
        sub: '',
        value: amt,
        moneda,
      };
    }).filter((r) => r.value > 0),
  };

  // ---- Cost drill-down: mirror of revenueDetails for the Costo Total card.
  // UPG-1 asked for the cost breakdown to explode instead of grouping
  // everything under "Costo Partidas" — the sub-rows here surface which
  // individual instructor, partida (Materiales, Licencias Minitab,
  // Administración Plataforma, viáticos, etc.), admin, commission and
  // donation contributes to each cost concept, in the partida's own
  // currency. Rendered by SummaryCards under the matching cost line
  // when the chevron is expanded.
  const costDetails = {
    pagoInstructores: (instructores || []).map((i) => {
      const hrs = Number(i.horas) || 0;
      const semanas = Number(i.semanas) || 0;
      const costoHora = Number(i.costo_hora) || 0;
      const cost = hrs * semanas * costoHora;
      const moneda = i.moneda || 'USD';
      return {
        label: i.instructor || '(sin nombre)',
        sub: `${money(hrs)} h × ${semanas} sem × ${moneda} ${money(costoHora)}`,
        value: cost,
        moneda,
      };
    }).filter((r) => r.value > 0),

    // Group partidas by their name (Materiales, Viáticos, Licencias Minitab,
    // Administración Plataforma, etc.) so the Box Score doesn't lump every
    // gasto into one bucket. If a category appears in more than one moneda
    // (rare, but possible) each currency gets its own line.
    costoPartidas: (() => {
      const buckets = new Map();
      (partidas || []).forEach((p) => {
        const total = Number(p.totalCost) || 0;
        if (total <= 0) return;
        const moneda = p.moneda || 'USD';
        const name = p.partida || 'Partida';
        const key = `${name}::${moneda}`;
        const b = buckets.get(key) || { label: name, count: 0, value: 0, moneda };
        b.count += 1;
        b.value += total;
        buckets.set(key, b);
      });
      return Array.from(buckets.values())
        .sort((a, b) => b.value - a.value)
        .map((b) => ({
          label: b.label,
          sub: b.count > 1 ? `${b.count} conceptos` : '',
          value: b.value,
          moneda: b.moneda,
        }));
    })(),

    comisionAbierto: (estudiantesAbierto || [])
      .filter((s) => Number(s.comision_vendedor) > 0)
      .map((s) => {
        const price = Number(s.precio) || 0;
        const pct = Number(s.comision_vendedor) || 0;
        const commission = (price * pct) / 100;
        const moneda = s.moneda || 'USD';
        const who = s.miembro_vendedor || s.partner_vendedor || 'Vendedor';
        return {
          label: `${who} · ${s.nombre || 'Curso'}`,
          sub: `${pct}% × ${moneda} ${money(price)}`,
          value: commission,
          moneda,
        };
      })
      .filter((r) => r.value > 0),

    adminCursoAbierto: (admins || []).map((a) => ({
      label: a.instructor || 'Admin',
      sub: '',
      value: Number(a.cantidad) || 0,
      moneda: a.moneda || 'USD',
    })).filter((r) => r.value > 0),

    adminCursoCerrado: (adminsCerrado || []).map((a) => ({
      label: a.instructor || 'Admin',
      sub: '',
      value: Number(a.cantidad) || 0,
      moneda: a.moneda || 'USD',
    })).filter((r) => r.value > 0),

    proyectoConCausa: (proyectoConCausa || []).map((c) => ({
      label: c.instructor || 'Proyecto con Causa',
      sub: '',
      value: Number(c.cantidad) || 0,
      moneda: c.moneda || 'USD',
    })).filter((r) => r.value > 0),

    comisionVenta: (comisionVenta || []).map((c) => ({
      label: c.instructor || 'Comisión',
      sub: '',
      value: Number(c.cantidad) || 0,
      moneda: c.moneda || 'USD',
    })).filter((r) => r.value > 0),
  };

  // Box Score concept → tab mapping. Click a concept in the Ingreso/Costo/
  // Utilidad breakdowns to jump to its section instead of scrolling by hand.
  const SECTION_TO_TAB = {
    instructores: 'instructores',
    pagoInstructores: 'instructores',
    cursoCerrado: 'cursos',
    adminCursoCerrado: 'cursos',
    cursoAbierto: 'cursos',
    adminCursoAbierto: 'cursos',
    comisionAbierto: 'cursos',
    partidas: 'partidas',
    costoPartidas: 'partidas',
    ingresoGeneral: 'partidas',
    proyectoConCausa: 'partidas',
    comisionVenta: 'partidas',
    honorarios: 'honorarios',
    utilidadPartners: 'honorarios',
    utilidadLSSI: 'honorarios',
  };
  const jumpToSection = (sectionKey) => {
    const tab = SECTION_TO_TAB[sectionKey];
    if (!tab) return;
    setActiveTab(tab);
    // Wait a frame for the tab content to mount, then scroll.
    requestAnimationFrame(() => {
      const el = document.getElementById('lssi-tab-strip');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // ---- Presentation helpers scoped to this render ----
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (_) { return '—'; }
  };
  const pageStyles = {
    page: { color: '#111827', fontFamily: '-apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif', minWidth: 0, maxWidth: '100%' },
    pageInner: { padding: '8px 4px 40px' },
    topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' },
    breadcrumb: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#dc3545', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 },
    pageTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' },
    selectorCard: { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 1px 3px rgba(15,23,42,0.03)', marginBottom: 20, overflow: 'hidden' },
    selectorHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' },
    selectorToggle: { border: '1px solid #e5e7eb', background: '#ffffff', color: '#374151', fontSize: '0.8rem', fontWeight: 500, padding: '4px 12px', borderRadius: 6, cursor: 'pointer' },
    selectorBody: { padding: 18 },
    emptyState: { textAlign: 'center', padding: '60px 20px', background: '#ffffff', border: '1px dashed #e5e7eb', borderRadius: 12, marginBottom: 20 },
    projectHeader: { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 1px 3px rgba(15,23,42,0.03)', padding: '20px 22px', marginBottom: 20 },
    projectHeaderTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
    projectName: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.015em', lineHeight: 1.2 },
    projectMeta: { display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 8 },
    codePill: { display: 'inline-block', padding: '3px 10px', background: '#eef2ff', color: '#3730a3', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', borderRadius: 4, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' },
    metaSeparator: { color: '#d1d5db' },
    exportBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', border: '1px solid #dc3545', background: '#ffffff', color: '#dc3545', fontSize: '0.875rem', fontWeight: 600, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s ease' },
    metaChips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 },
    searchBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 20 },
    searchInput: { flex: 1, border: 'none', outline: 'none', background: 'transparent', color: '#111827', fontSize: '0.9rem', fontFamily: 'inherit' },
    searchCount: (hit) => ({ fontSize: '0.75rem', fontWeight: 600, color: hit ? '#166534' : '#6b7280', background: hit ? '#dcfce7' : '#f3f4f6', padding: '2px 10px', borderRadius: 999 }),
    searchClear: { background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '1rem', padding: '4px 6px' },
    tabsBar: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb', overflowX: 'auto', flexWrap: 'nowrap' },
  };

  const modalidadHasCerrado = selectedProyecto?.modalidad?.includes?.('Cerrado');
  const modalidadHasAbierto = selectedProyecto?.modalidad?.includes?.('Abierto');

  return (
    <div style={pageStyles.page}>
      {/* Sticky KPI bar when scrolled */}
      <StickySummaryBar
        ingreso={totalIngreso}
        costo={totalCost}
        utilidad={utilidad}
        margen={margen}
        visible={isScrolled}
        proyectoName={selectedProyecto?.name}
      />

      <div style={pageStyles.pageInner}>
        {/* Breadcrumb + page title */}
        <div style={pageStyles.topBar}>
          <Link to="/proyectos" style={pageStyles.breadcrumb}>
            <IconChevronLeft /> Volver a la lista
          </Link>
          <h1 style={pageStyles.pageTitle}>Detalle Proyecto</h1>
        </div>

        {/* Filters + project picker (collapsible when a project is loaded) */}
        <div style={pageStyles.selectorCard}>
          {/* Whole header toggles the selector — users click the
              "Cambiar proyecto" label, not just the small Abrir/Cerrar button. */}
          <div
            style={{ ...pageStyles.selectorHeader, cursor: selectedProyecto ? 'pointer' : 'default', userSelect: 'none' }}
            onClick={() => { if (selectedProyecto) setSelectorOpen((v) => !v); }}
            role={selectedProyecto ? 'button' : undefined}
            tabIndex={selectedProyecto ? 0 : undefined}
            onKeyDown={(e) => {
              if (selectedProyecto && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setSelectorOpen((v) => !v);
              }
            }}
            aria-expanded={selectedProyecto ? selectorOpen : undefined}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#374151', fontWeight: 600, fontSize: '0.9rem' }}>
              <IconFilter />
              {selectedProyecto ? 'Cambiar proyecto' : 'Selecciona un proyecto'}
            </div>
            {selectedProyecto && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSelectorOpen((v) => !v); }}
                style={pageStyles.selectorToggle}
              >
                {selectorOpen ? 'Cerrar' : 'Abrir'}
              </button>
            )}
          </div>
          {(selectorOpen || !selectedProyecto) && (
            <div style={pageStyles.selectorBody}>
              <FiltersRow
        modalidadOptions={[
          ...new Set((proyectos || []).map((p) => p.modalidad).filter(Boolean)),
        ]}
        liderOptions={[
          ...new Set((proyectos || []).map((p) => p.lider).filter(Boolean)),
        ]}
        statusOptions={[
          ...new Set((proyectos || []).map((p) => p.status).filter(Boolean)),
        ]}
        cityOptions={[
          ...new Set((proyectos || []).map((p) => p.ciudad).filter(Boolean)),
        ]}
        countryOptions={[
          ...new Set(
            (proyectos || [])
              .map((p) => normalizeCountry(p.pais))
              .filter(Boolean)
          ),
        ]}
        sedes={sedes}
        selectedModalidad={selectedModalidad}
        setSelectedModalidad={setSelectedModalidad}
        selectedLider={selectedLider}
        setSelectedLider={setSelectedLider}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        selectedSede={selectedSede}
        setSelectedSede={setSelectedSede}
      />

              <div style={{ marginTop: 16 }}>
                <Label style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151', letterSpacing: '0.02em' }}>
                  Selecciona Proyecto o Curso
                </Label>
                <Select
                  options={proyectoOptions}
                  onChange={handleProyectoSelect}
                  placeholder="Selecciona un Proyecto..."
                  isSearchable
                  isClearable
                  value={
                    selectedProyecto
                      ? { label: selectedProyecto.name, value: selectedProyecto }
                      : null
                  }
                  {...SELECT_MENU_PORTAL_PROPS}
                />
              </div>
            </div>
          )}
        </div>

        {/* Empty state when nothing has been picked yet */}
        {!selectedProyecto && (
          <div style={pageStyles.emptyState}>
            <div style={{ fontSize: 42, opacity: 0.35, marginBottom: 10 }}>📂</div>
            <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
              Selecciona un proyecto arriba para ver su detalle, KPIs y desglose por sección.
            </p>
          </div>
        )}

        {/* Loading */}
        {selectedProyecto && loading && (
          <div>
            <div className="text-center mb-3">
              <Spinner color="primary" />
              <p className="mt-2">Cargando datos del proyecto...</p>
            </div>
            <SectionSkeleton />
            <SectionSkeleton />
          </div>
        )}

        {/* Error */}
        {selectedProyecto && !loading && err && (
          <div className="error-container">
            <p style={{ color: "crimson" }}>❌ {err}</p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setRetryCount(prev => prev + 1);
                fetchDataForProyecto(selectedProyecto, true);
              }}
              disabled={loading}
            >
              🔄 Reintentar {retryCount > 0 && `(Intento ${retryCount + 1})`}
            </button>
          </div>
        )}

        {/* Loaded — project header, search, KPIs, tabs, tab content */}
        {selectedProyecto && !loading && !err && (
          <>
            {/* Project header — name + code + metadata chips + export */}
            <header style={pageStyles.projectHeader}>
              <div style={pageStyles.projectHeaderTop}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h2 style={pageStyles.projectName}>{selectedProyecto.name}</h2>
                  <div style={pageStyles.projectMeta}>
                    <span style={pageStyles.codePill}>{selectedProyecto.proyecto_code?.toUpperCase?.() || ''}</span>
                    <span style={pageStyles.metaSeparator}>·</span>
                    <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{selectedProyecto.modalidad || 'Sin modalidad'}</span>
                  </div>
                </div>
                <button onClick={handleExportExcel} style={pageStyles.exportBtn} title="Exportar resumen del proyecto a Excel">
                  <IconExport /> Exportar Resumen
                </button>
              </div>
              <div style={pageStyles.metaChips}>
                <MetaChip label="Líder" value={loadedLeader || selectedProyecto.lider || '—'} />
                <MetaChip label="Value Stream" value={selectedProyecto.sede_name || selectedProyecto.sede_code || '—'} />
                <MetaChip label="País" value={`${selectedProyecto.pais || '—'}${selectedProyecto.ciudad ? ' · ' + selectedProyecto.ciudad : ''}`} />
                <MetaChip label="Inicio" value={fmtDate(selectedProyecto.inicio_date)} />
                <MetaChip label="Fin" value={fmtDate(selectedProyecto.terminacion_date)} />
              </div>
            </header>

            {/* Search bar */}
            <div style={pageStyles.searchBar}>
              <input
                type="text"
                placeholder="🔍 Buscar instructor, partida, estudiante, concepto…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={pageStyles.searchInput}
              />
              {searchTerm && (
                <>
                  <span style={pageStyles.searchCount(totalSearchResults > 0)}>{totalSearchResults} resultados</span>
                  <button onClick={() => setSearchTerm('')} style={pageStyles.searchClear} aria-label="Limpiar búsqueda">✕</button>
                </>
              )}
            </div>

            {/* Box Score */}
            <SummaryCards
                // Revenue
                ingresoTotal={totalIngreso}
                ingresoInstructores={detalle.ingreso_instructores || 0}
                ingresoCursoCerrado={detalle.ingreso_curso_cerrado || 0}
                ingresoPartidas={detalle.partidas_ingreso || 0}
                ingresoAbierto={detalle.ingreso_abierto || 0}
                ingresoGeneral={detalle.ingreso_general || 0}

                // Costs
                costoTotal={totalCost}
                costoInstructores={detalle.costo_instructores || 0}
                costoPartidas={detalle.partidas_costo || 0}
                comisionAbierto={detalle.comision_abierto || 0}
                adminCursoAbierto={detalle.admin_curso_abierto || 0}
                adminCursoCerrado={detalle.admin_curso_cerrado || 0}
                proyectoConCausa={detalle.proyecto_con_causa || 0}
                comisionVenta={detalle.comision_venta || 0}

                // Counts
                numInstructores={instructores.length}
                numEstudiantes={estudiantesAbierto.length}
                numPartidas={partidas.length}

                // Calculated
                utilidad={utilidad}
                margen={margen}

                // Profit distribution (admin only)
                utilidadPartners={utilidadPartners}
                utilidadLSSI={utilidadLSSI}
                isAdmin={isAdmin}

                // Original currency - accurate profit from backend
                originalCurrency={primaryCurrency ? `${primaryCurrency} ` : null}
                originalIngresoTotal={revenueInOriginalCurrency}
                originalCostoTotal={costInOriginalCurrency}
                originalUtilidad={profitInOriginalCurrency}
                profitByCurrency={profitByCurrency}

                // Revenue drill-down: per-source detail rows for the
                // expanded Ingreso Total card.
                revenueDetails={revenueDetails}
                costDetails={costDetails}

                // Click-through: each concept jumps to its section tab.
                onSectionClick={jumpToSection}
              />

            {/* Tab strip */}
            <div id="lssi-tab-strip" style={pageStyles.tabsBar} role="tablist" aria-label="Secciones del proyecto">
              <TabButton
                active={activeTab === 'instructores'}
                onClick={() => setActiveTab('instructores')}
                icon={<IconEquipo />}
                label="Instructores"
                count={instructores.length}
                highlight={!!searchResults.instructores}
              />
              <TabButton
                active={activeTab === 'cursos'}
                onClick={() => setActiveTab('cursos')}
                icon={<IconCurso />}
                label="Cursos"
                count={(partidasCurso.length || 0) + (estudiantesAbierto.length || 0)}
                highlight={!!(searchResults.cursoCerrado || searchResults.cursoAbierto)}
              />
              <TabButton
                active={activeTab === 'partidas'}
                onClick={() => setActiveTab('partidas')}
                icon={<IconPartida />}
                label="Partidas"
                count={(partidas.length || 0) + (proyectoConCausa.length || 0) + (ingresoGeneral.length || 0)}
                highlight={!!(searchResults.partidas || searchResults.proyectoConCausa || searchResults.ingresoGeneral)}
              />
              <TabButton
                active={activeTab === 'honorarios'}
                onClick={() => setActiveTab('honorarios')}
                icon={<IconHonorarios />}
                label="Utilidad"
                count={instructores.length}
              />
            </div>

            {/* Tab content */}
            {activeTab === 'instructores' && (
              <SectionCard
                title="Instructores"
                icon={<IconEquipo />}
                count={searchTerm ? filteredInstructores.length : instructores.length}
                type="instructores"
                searchHighlight={!!searchResults.instructores}
              >
                <Row>
                  <Col>
                    <ProyectoIngresoModal
                      create
                      resetState={resetState}
                      proyectoCodeSelected={selectedProyecto.proyecto_code}
                      proyectoNameSelected={selectedProyecto.name}
                      proyectoMoneda={selectedProyecto.moneda || 'USD'}
                      selectedProyecto={selectedProyecto}
                    />
                  </Col>
                </Row>

                <div style={{ marginTop: 20 }}>
                  <h3>Número de Instructores: {searchTerm ? `${filteredInstructores.length} de ${instructores.length}` : instructores.length}</h3>
                  <h3>
                    Ingreso de Instructores:{" "}
                    <CurrencyDisplay
                      originalAmounts={currencyBreakdown.ingreso_instructores}
                      usdTotal={detalle.ingreso_instructores || 0}
                    />
                  </h3>
                  <h3>
                    Costo de Instructores:{" "}
                    <CurrencyDisplay
                      originalAmounts={currencyBreakdown.costo_instructores}
                      usdTotal={detalle.costo_instructores || 0}
                    />
                  </h3>
                </div>

                <Row>
                  <Col>
                    <ProyectoDetailInstructorList
                      instructores={searchTerm ? filteredInstructores : instructores}
                      resetState={resetState}
                      itemsPerPage={8}
                      selectedProyecto={selectedProyecto}
                      proyectoCodeSelected={selectedProyecto.proyecto_code}
                      proyectoNameSelected={selectedProyecto.name}
                    />
                  </Col>
                </Row>
              </SectionCard>
            )}

            {activeTab === 'cursos' && (
              <>
                {!modalidadHasCerrado && !modalidadHasAbierto && (
                  <div style={pageStyles.emptyState}>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                      Este proyecto no tiene modalidad de Curso Abierto ni Curso Cerrado.
                    </p>
                  </div>
                )}
                {modalidadHasCerrado && (
                  <SectionCard
                    title="Curso Cerrado"
                    icon={<IconCurso />}
                    count={searchTerm ? filteredPartidasCurso.length : partidasCurso.length}
                    type="cursos"
                    searchHighlight={!!searchResults.cursoCerrado}
                  >
                    <Row>
                      <Col>
                        <AdminCursoListCerrado
                          proyectoCodeSelected={selectedProyecto.proyecto_code}
                          proyectoNameSelected={selectedProyecto.name}
                          partners={partners}
                          resetState={resetState}
                          admins={adminsCerrado}
                          ingresoProyecto={revenueInOriginalCurrency ?? totalIngreso}
                          estudiantesIngreso={revenueInOriginalCurrency ?? totalIngreso}
                          proyectoMoneda={selectedProyecto.moneda || 'USD'}
                          selectedProyecto={selectedProyecto}
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <ProyectoCursoModal
                          create
                          resetState={resetState}
                          proyectoCodeSelected={selectedProyecto.proyecto_code}
                          proyectoNameSelected={selectedProyecto.name}
                          proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        />
                      </Col>
                    </Row>

                    <div style={{ marginTop: 20 }}>
                      <h3>
                        Ingreso del Curso Cerrado:{" "}
                        <CurrencyDisplay
                          originalAmounts={currencyBreakdown.ingreso_curso_cerrado}
                          usdTotal={detalle.ingreso_curso_cerrado || 0}
                        />
                      </h3>
                    </div>

                    <Row>
                      <Col>
                        <ProyectoDetailCursoList
                          partidas_curso={searchTerm ? filteredPartidasCurso : partidasCurso}
                          resetState={resetState}
                          proyectoMoneda={selectedProyecto.moneda || 'USD'}
                          itemsPerPage={8}
                        />
                      </Col>
                    </Row>

                    <hr />

                    <Row>
                      <Col>
                        <h2>Administrador del Curso Cerrado</h2>
                      </Col>
                      <Col>
                        <AdminCursoModalCerrado
                          create
                          resetState={resetState}
                          proyectoCodeSelected={selectedProyecto.proyecto_code}
                          proyectoNameSelected={selectedProyecto.name}
                          partners={partners}
                          estudiantesIngreso={revenueInOriginalCurrency ?? totalIngreso}
                          proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        />
                      </Col>
                    </Row>
                  </SectionCard>
                )}
                {modalidadHasAbierto && (
                  <SectionCard
                    title="Curso Abierto"
                    icon={<IconCurso />}
                    count={searchTerm ? filteredEstudiantesAbierto.length : estudiantesAbierto.length}
                    type="cursos"
                    searchHighlight={!!searchResults.cursoAbierto}
                  >
                    <Row>
                      <Col>
                        <CursoAbiertoModal
                          create
                          resetState={resetState}
                          proyectoCodeSelected={selectedProyecto.proyecto_code}
                          proyectoNameSelected={selectedProyecto.name}
                          proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        />
                      </Col>
                    </Row>

                    <div style={{ marginTop: 20 }}>
                      <h3>Estudiantes: {searchTerm ? `${filteredEstudiantesAbierto.length} de ${estudiantesAbierto.length}` : estudiantesAbierto.length}</h3>
                      <h3>
                        Ingreso del Curso Abierto:{" "}
                        <CurrencyDisplay
                          originalAmounts={currencyBreakdown.ingreso_abierto}
                          usdTotal={detalle.ingreso_abierto || 0}
                        />
                      </h3>
                    </div>

                    <Row>
                      <Col>
                        <CursoAbiertoList
                          estudiantes_curso_abierto={searchTerm ? filteredEstudiantesAbierto : estudiantesAbierto}
                          resetState={resetState}
                          proyectoCodeSelected={selectedProyecto.proyecto_code}
                          proyectoNameSelected={selectedProyecto.name}
                          proyectoMoneda={selectedProyecto.moneda || 'USD'}
                          utilidad={utilidad}
                          itemsPerPage={8}
                        />
                      </Col>
                    </Row>

                    <hr />

                    <div>
                      <Row>
                        <Col>
                          <h2>Comisión de Venta Proyectos - Cursos Abiertos</h2>
                          <Row>
                            <Col>
                              <ComisionVentaModal
                                create
                                resetState={resetState}
                                proyectoCodeSelected={selectedProyecto.proyecto_code}
                                proyectoNameSelected={selectedProyecto.name}
                                partners={partners}
                                estudiantesIngreso={revenueInOriginalCurrency ?? totalIngreso}
                                proyectoMoneda={selectedProyecto.moneda || 'USD'}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <ComisionVentaList
                                proyectoCodeSelected={selectedProyecto.proyecto_code}
                                proyectoNameSelected={selectedProyecto.name}
                                partners={partners}
                                resetState={resetState}
                                admins={searchTerm ? filteredComisionVenta : comisionVenta}
                                estudiantesIngreso={revenueInOriginalCurrency ?? totalIngreso}
                                proyectoMoneda={selectedProyecto.moneda || 'USD'}
                              />
                            </Col>
                          </Row>
                        </Col>
                      </Row>

                      <hr />

                      <div>
                        <Row>
                          <Col>
                            <h2>Administrador del Curso</h2>
                            <Row>
                              <Col>
                                <AdminCursoModal
                                  create
                                  resetState={resetState}
                                  proyectoCodeSelected={selectedProyecto.proyecto_code}
                                  proyectoNameSelected={selectedProyecto.name}
                                  partners={partners}
                                  ingresoProyecto={revenueInOriginalCurrency ?? totalIngreso}
                                  proyectoMoneda={selectedProyecto.moneda || 'USD'}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <AdminCursoList
                                  proyectoCodeSelected={selectedProyecto.proyecto_code}
                                  proyectoNameSelected={selectedProyecto.name}
                                  partners={partners}
                                  resetState={resetState}
                                  admins={admins}
                                  ingresoProyecto={revenueInOriginalCurrency ?? totalIngreso}
                                  proyectoMoneda={selectedProyecto.moneda || 'USD'}
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </SectionCard>
                )}
              </>
            )}

            {activeTab === 'partidas' && (
              <>
                <SectionCard
                  title="Partidas Directo"
                  icon={<IconPartida />}
                  count={searchTerm ? filteredPartidas.length : partidas.length}
                  type="partidas"
                  searchHighlight={!!searchResults.partidas}
                >
                  <Row>
                    <Col>
                      <ProyectoCostoModal
                        create
                        resetState={resetState}
                        proyectoCodeSelected={selectedProyecto.proyecto_code}
                        proyectoNameSelected={selectedProyecto.name}
                        proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        ingresoProyecto={revenueInOriginalCurrency ?? totalIngreso}
                        instructores={partners}
                      />
                    </Col>
                  </Row>

                  <div style={{ marginTop: 20 }}>
                    <h3>Número de Partidas: {searchTerm ? `${filteredPartidas.length} de ${partidas.length}` : partidas.length}</h3>
                    <h3>
                      Total Ingreso:{" "}
                      <CurrencyDisplay
                        originalAmounts={currencyBreakdown.partidas_ingreso}
                        usdTotal={detalle.partidas_ingreso || 0}
                      />
                    </h3>
                    <h3>
                      Total Costo:{" "}
                      <CurrencyDisplay
                        originalAmounts={currencyBreakdown.partidas_costo}
                        usdTotal={detalle.partidas_costo || 0}
                      />
                    </h3>
                  </div>

                  <Row>
                    <Col>
                      <ProyectoDetailPartidasList
                        partidas={searchTerm ? filteredPartidas : partidas}
                        instructores={instructores}
                        resetState={resetState}
                        itemsPerPage={8}
                        selectedProyecto={selectedProyecto}
                        ingresoProyecto={revenueInOriginalCurrency ?? totalIngreso}
                      />
                    </Col>
                  </Row>
                </SectionCard>

                <SectionCard
                  title="Proyecto con Causa"
                  icon={<IconPartida />}
                  count={searchTerm ? filteredProyectoConCausa.length : proyectoConCausa.length}
                  type="comisiones"
                  searchHighlight={!!searchResults.proyectoConCausa}
                >
                  <Row>
                    <Col>
                      <ProyectoConCausaModal
                        create
                        resetState={resetState}
                        proyectoCodeSelected={selectedProyecto.proyecto_code}
                        proyectoNameSelected={selectedProyecto.name}
                        proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        estudiantesIngreso={revenueInOriginalCurrency ?? totalIngreso}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <ProyectoConCausaList
                        proyectoCodeSelected={selectedProyecto.proyecto_code}
                        proyectoNameSelected={selectedProyecto.name}
                        selectedProyecto={selectedProyecto}
                        resetState={resetState}
                        admins={searchTerm ? filteredProyectoConCausa : proyectoConCausa}
                        estudiantesIngreso={revenueInOriginalCurrency ?? totalIngreso}
                      />
                    </Col>
                  </Row>
                </SectionCard>

                <SectionCard
                  title="Ingresos en General"
                  icon={<IconPartida />}
                  count={searchTerm ? filteredIngresoGeneral.length : ingresoGeneral.length}
                  type="comisiones"
                  searchHighlight={!!searchResults.ingresoGeneral}
                >
                  <Row>
                    <Col>
                      <IngresoGeneralModal
                        create
                        resetState={resetState}
                        proyectoCodeSelected={selectedProyecto.proyecto_code}
                        proyectoNameSelected={selectedProyecto.name}
                        proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        partners={partners}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <IngresoGeneralList
                        proyectoCodeSelected={selectedProyecto.proyecto_code}
                        proyectoNameSelected={selectedProyecto.name}
                        proyectoMoneda={selectedProyecto.moneda || 'USD'}
                        partners={partners}
                        resetState={resetState}
                        admins={searchTerm ? filteredIngresoGeneral : ingresoGeneral}
                      />
                    </Col>
                  </Row>
                </SectionCard>
              </>
            )}

            {activeTab === 'honorarios' && (
              <SectionCard
                title="Reparto de Utilidades"
                icon={<IconHonorarios />}
                count={instructores.length}
                type="honorarios"
              >
                <Row>
                  <Col>
                    <HonorariosList
                      ref={honorariosListRef}
                      proyectoNameSelected={selectedProyecto.name}
                      partidas={partidas}
                      instructores={instructores}
                      instructoresProportions={instructorsWithTotalIncome}
                      estudiantes_curso_abierto={estudiantesAbierto}
                      resetState={resetState}
                      itemsPerPage={8}
                      utilidad={utilidad}
                      proyectoCodeSelected={selectedProyecto.proyecto_code}
                      proyectoModalidadSelected={selectedProyecto.modalidad}
                      proyectoLider={selectedProyecto.lider}
                      onLeaderLoaded={handleLeaderLoaded}
                      totalCostoInstructores={detalle.costo_instructores || 0}
                      partidaCosto={detalle.partidas_costo || 0}
                      admin_total_cost={detalle.admin_curso_abierto || 0}
                      total_cost_proyect_con_causa={detalle.proyecto_con_causa || 0}
                      utilities={utilities}
                      totalCantidad={utilidadPartners}
                      utilidades_byproject={utilidadesByProject}
                      abierto_miembros={abiertoMiembros}
                      filteredlider={filteredLider}
                      onSaveComplete={resetState}
                      primaryCurrency={primaryCurrency}
                      profitByCurrency={profitByCurrency}
                      profitInOriginalCurrency={profitInOriginalCurrency}
                      revenueInOriginalCurrency={revenueInOriginalCurrency}
                      utilidadUSD={utilidad}
                      isAdmin={isAdmin}
                    />
                  </Col>
                </Row>
              </SectionCard>
            )}
          </>
        )}

        {/* Back to Top button */}
        {showBackToTop && (
          <button
            className="back-to-top-btn"
            onClick={scrollToTop}
            title="Volver arriba"
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 1000,
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              boxShadow: '0 4px 14px rgba(220, 53, 69, 0.35)',
              cursor: 'pointer',
              fontSize: '20px',
              fontWeight: 700,
            }}
          >
            ↑
          </button>
        )}
      </div>
    </div>
  );
}

export default DetalleProyectos;